import { equals, flatten, has, last, max, pluck } from "ramda";
import { isArray, isNotNilOrEmpty } from "ramda-adjunct";
import { BasicOptions, NestedOptions } from "@sinch/components/form/MultilevelSelect/types";

/**
 * Flattens nested options into a single-level array for search functionality.
 */
export const flatOptions = (options: NestedOptions[]) =>
  flattenTree(options).map((row) => ({
    value: last(flatten(pluck("value")(row))),
    label: flatten(pluck("label")(row)),
  }));
/**
 * Recursively flattens a tree of nested options into an array of paths.value
 */
const flattenTree = (options: NestedOptions[]): NestedOptions[][] =>
  options.reduce((acc, { value, label, children }) => {
    if (isNotNilOrEmpty(children) && isArray(children)) {
      return [...acc, ...flattenTree(children!).map((childrenList) => [{ value, label }, ...childrenList])];
    }
    return [...acc, [{ value, label }]];
  }, [] as NestedOptions[][]);
/**
 * Calculates the maximum depth of a nested options tree.
 */
export const getDepth = (list: NestedOptions[]) => {
  let maxDepth = 1;
  for (const item of list) {
    if (has("children", item) && isNotNilOrEmpty(item.children)) {
      maxDepth = max(maxDepth, 1 + getDepth(isArray(item.children) ? item.children! : []));
    }
  }
  return maxDepth;
};
/**
 * Recursively searches for a target value in a tree of nodes.
 */
export const searchPath = <TVal,>(nodes: NestedOptions<TVal>[], target: TVal): number[] | null => {
  for (let i = 0; i < nodes.length; i++) {
    const { value, children } = nodes[i];
    if (equals(value, target)) {
      return [i];
    }
    if (isArray(children)) {
      const path = searchPath<TVal>(children as NestedOptions<TVal>[], target);
      if (path) {
        return [i, ...path];
      }
    }
  }
  return null;
};
/**
 * Recursively searches for a target value and returns the labels of every node on the path to it.
 */
export const searchLabelPath = <TVal,>(nodes: NestedOptions<TVal>[], target: TVal): string[] | null => {
  for (const { value, label, children } of nodes) {
    if (equals(value, target)) {
      return flatten([label]);
    }
    if (isArray(children)) {
      const path = searchLabelPath<TVal>(children as NestedOptions<TVal>[], target);
      if (path) {
        return [...flatten([label]), ...path];
      }
    }
  }
  return null;
};
export const getLeafList = <TVal, R extends Record<string, any> = BasicOptions<TVal>>(
  nodes: NestedOptions<TVal, R>[]
): NestedOptions<TVal, R>[] | null => {
  const list = [];
  for (const node of nodes) {
    const { children } = node;
    if (isArray(children)) {
      const nestedNode = getLeafList<TVal, R>(children as NestedOptions<TVal, R>[]) ?? [];
      list.push(...nestedNode);
    } else {
      list.push(node);
    }
  }
  return list;
};
export const findItem = <TVal,>(nodes: NestedOptions<TVal>[], target: TVal): NestedOptions<TVal> | null => {
  for (const node of nodes) {
    const { children } = node;
    if (equals(node.value, target)) {
      return node;
    }
    if (isArray(children)) {
      const nestedNode = findItem<TVal>(children as NestedOptions<TVal>[], target);
      if (nestedNode) {
        return nestedNode;
      }
    }
  }
  return null;
};
